// All of the data-overlay session state for one open pedigree (TGCLOUD-1670).
//
// Lives in a hook rather than in PedigreeChart because there are three
// consumers of the same state — the Overlays popover, the results row and the
// canvas paint pass — and because a Copilot request has to land in exactly the
// same place a click does.
//
// Nothing here touches `members`, `memberRecords`, `labelsSet` or `options`.
// That is the load-bearing constraint: `members` is a layout-worker input
// (seventeen seconds on the largest real family), `memberRecords` is the
// wholesale save payload, and `labelsSet` is a render-effect dependency, so
// routing overlay state through any of them would make turning an overlay on
// rebuild the entire SVG or, worse, write display state into shared records.
// Results reach the canvas by ref plus a version counter instead.

import React from 'react';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { presetById } from './catalogue';
import {
  materialiseCriteria,
  evaluateDataOverlay,
  applySlotOptions,
  defaultParams
} from './evaluate';
import { resolvePersons } from './resolvePersons';
import { deriveProxyNodes, proxyIntentsOf } from '../pedigree/deriveProxies';
import useOverlayFacts from './useOverlayFacts';
import { useStatusMeanings } from 'src/hooks/useLookups';
import { LIST_BY_FIELD } from 'src/util/statusSemantics';
import { MAX_DATA_OVERLAY_SLOTS, slotColour, emptySlot } from './slots';

// Every coded status list a preset compares against.
const STATUS_LISTS = Object.values(LIST_BY_FIELD);

const DEFAULT_SLOT_OPTIONS = Object.freeze({
  countHidden: true,
  markEveryRendering: true
});

const startOfNextDay = (now) => {
  const d = new Date(now);
  d.setHours(24, 0, 0, 0);
  return d;
};

/**
 * The evaluation day, as a LOCAL calendar day.
 *
 * "Overdue" is a statement about today, so a chart left open across midnight
 * would otherwise keep answering yesterday's question. Re-stamped on the local
 * midnight boundary and whenever the tab is brought back to the front, which is
 * the frame the home Overdue panel already works in.
 */
function useAsOf() {
  const [asOf, setAsOf] = React.useState(() => new Date());

  React.useEffect(() => {
    let timer = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      const ms = startOfNextDay(new Date()).getTime() - Date.now();
      timer = setTimeout(() => {
        setAsOf(new Date());
        schedule();
      }, Math.max(1000, ms));
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') setAsOf(new Date());
    };
    schedule();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return asOf;
}

export default function useDataOverlays({
  rootId,
  familyId,
  memberRecords = [],
  canvasMembers = [],
  anonymous = false,
  diagnoses = []
} = {}) {
  const fmtDate = useDateFormatter();
  // The tooltips' words, in the reader's language.
  const t = useAutoTranslation();
  const asOf = useAsOf();

  const [slots, setSlots] = React.useState([]);
  const [treatment, setTreatment] = React.useState('ring');
  // The facts read is lazy. Opening the popover or applying an overlay from the
  // Copilot is what turns it on; a pedigree nobody overlays never issues it.
  const [activated, setActivated] = React.useState(false);
  const activate = React.useCallback(() => setActivated(true), []);

  const enabled = activated || slots.length > 0;
  const { facts, isLoading, isFetching, error, unavailable, refresh } =
    useOverlayFacts(rootId, { enabled, familyId });

  // The presets compare against whatever wording this group's status rows
  // carry for a meaning, disabled rows included (TGCLOUD-1751). Read on the
  // same lazy trigger as the facts, and re-evaluated when they arrive.
  const statusVersion = useStatusMeanings(STATUS_LISTS, { enabled });

  // Every DRAWN node, proxy clones included, derived exactly as ChartBox derives
  // what it renders. A person drawn twice must carry the mark twice, or the
  // second rendering reads as a gap that is not there.
  const canvasMembersWithProxies = React.useMemo(
    () => deriveProxyNodes(canvasMembers, proxyIntentsOf(memberRecords)),
    [canvasMembers, memberRecords]
  );

  const personIndex = React.useMemo(
    () =>
      resolvePersons({
        memberRecords,
        canvasMembersWithProxies,
        anonymous
      }),
    [memberRecords, canvasMembersWithProxies, anonymous]
  );

  // The roster INCLUDES hidden people on purpose: an absence overlay whose
  // roster is only what is on screen would report a family as complete because
  // the incomplete member is not being drawn.
  const evaluated = React.useMemo(() => {
    // `statusVersion` is read by materialiseCriteria through the status
    // registry, not through a variable, so it is named here to be a dep.
    void statusVersion;
    return slots.map((slot) => {
      const preset = presetById(slot.presetId);
      if (!preset) return { ...slot, preset: null, results: null };
      // A facts-backed overlay evaluated against a payload that has not
      // arrived, or that the caller's role may not read, would answer
      // "nobody" for the wrong reason. Withhold the result instead.
      const needsFacts = preset.roles === 'facts';
      if (needsFacts && (!facts || unavailable?.notes)) {
        return { ...slot, preset, results: null };
      }
      try {
        const criteria = materialiseCriteria(preset, slot.params);
        const results = evaluateDataOverlay({
          preset,
          criteria,
          facts,
          personIndex,
          asOf,
          fmtDate,
          t
        });
        return {
          ...slot,
          preset,
          criteria,
          results: applySlotOptions(results, slot.options)
        };
      } catch (_) {
        return { ...slot, preset, results: null };
      }
    });
  }, [slots, facts, unavailable, personIndex, asOf, fmtDate, t, statusVersion]);

  const painted = React.useMemo(
    () =>
      evaluated.map((slot, index) => ({
        ...slot,
        colour: slotColour(index)
      })),
    [evaluated]
  );

  // The canvas reads results from a ref and repaints on a version bump, so
  // toggling an overlay never re-runs the heavy render effect or the layout.
  const slotsRef = React.useRef([]);
  const styleRef = React.useRef({ treatment: 'ring' });
  const [version, setVersion] = React.useState(0);

  React.useEffect(() => {
    slotsRef.current = painted.filter((s) => s.enabled && s.results);
    styleRef.current = { treatment };
    setVersion((v) => v + 1);
  }, [painted, treatment]);

  const addSlot = React.useCallback((presetId) => {
    const preset = presetById(presetId);
    if (!preset) return;
    setActivated(true);
    setSlots((prev) => {
      if (prev.length >= MAX_DATA_OVERLAY_SLOTS) return prev;
      if (prev.some((s) => s.presetId === presetId)) return prev;
      return [
        ...prev,
        {
          ...emptySlot(presetId, defaultParams(preset)),
          options: { ...DEFAULT_SLOT_OPTIONS }
        }
      ];
    });
  }, []);

  const removeSlot = React.useCallback((index) => {
    setSlots((prev) => prev.filter((_s, i) => i !== index));
  }, []);

  const toggleSlot = React.useCallback((index) => {
    setSlots((prev) =>
      prev.map((s, i) => (i === index ? { ...s, enabled: !s.enabled } : s))
    );
  }, []);

  const updateSlot = React.useCallback((index, { params, options }) => {
    setSlots((prev) =>
      prev.map((s, i) =>
        i === index
          ? {
              ...s,
              params: params ?? s.params,
              options: options ?? s.options,
              enabled: true
            }
          : s
      )
    );
  }, []);

  /**
   * Apply a preset by id, replacing slot A when both slots are taken. This is
   * the single entry point a Copilot request lands on, so a chip click and a
   * click in the picker cannot diverge.
   */
  const applyPreset = React.useCallback((presetId, params) => {
    const preset = presetById(presetId);
    if (!preset) return false;
    setActivated(true);
    setSlots((prev) => {
      const merged = { ...defaultParams(preset), ...(params || {}) };
      const existing = prev.findIndex((s) => s.presetId === presetId);
      const next = {
        presetId,
        params: merged,
        options: { ...DEFAULT_SLOT_OPTIONS },
        enabled: true
      };
      if (existing >= 0) {
        return prev.map((s, i) => (i === existing ? { ...s, ...next } : s));
      }
      if (prev.length < MAX_DATA_OVERLAY_SLOTS) return [...prev, next];
      return [next, ...prev.slice(1)];
    });
    return true;
  }, []);

  // Next/previous stepping through the on-canvas matches of one slot.
  const [cursors, setCursors] = React.useState([]);
  const step = React.useCallback(
    (index, delta) => {
      const onCanvas = (painted[index]?.results?.matches || []).filter(
        (m) => m.onCanvas
      );
      if (!onCanvas.length) return null;
      const current = cursors[index] || 0;
      const len = onCanvas.length;
      const next = (((current + delta) % len) + len) % len;
      setCursors((prev) => {
        const copy = [...prev];
        copy[index] = next;
        return copy;
      });
      return onCanvas[next]?.pids?.[0] ?? null;
    },
    [painted, cursors]
  );

  const pidsFor = React.useCallback(
    (index) =>
      (painted[index]?.results?.matches || [])
        .filter((m) => m.onCanvas)
        .flatMap((m) => m.pids),
    [painted]
  );

  return {
    slots: painted,
    rawSlots: slots,
    treatment,
    setTreatment,
    facts,
    factsLoading: isLoading || (isFetching && !facts),
    factsError: error,
    unavailable,
    personIndex,
    asOf,
    fmtDate,
    diagnoses,
    slotsRef,
    styleRef,
    version,
    activate,
    refresh,
    addSlot,
    removeSlot,
    toggleSlot,
    updateSlot,
    applyPreset,
    cursors,
    step,
    pidsFor
  };
}
